import { useEffect, useState } from 'react'
import { listIndicators } from '~/api/monitoring'
import type { MonitoringIndicator } from '~/api/types'
import { ApiIngestPanel } from './api-ingest-panel'
import { CsvIngestPanel } from './csv-ingest-panel'
import { ImageIngestPanel } from './image-ingest-panel'
import { IngestHistory } from './ingest-history'

type Tab = 'upload' | 'history'

/** 연동설정 — 체크리스트 정량지표에 소스 컬럼을 매핑하고 측정값을 적재한다. */
export function SettingsSection({ dashboardId }: { dashboardId: string }) {
    const [indicators, setIndicators] = useState<MonitoringIndicator[]>([])
    const [tab, setTab] = useState<Tab>('upload')
    // 업로드/삭제 성공 시 적재 이력을 다시 불러오도록 트리거(값이 바뀌면 재조회).
    const [historyKey, setHistoryKey] = useState(0)
    const bumpHistory = () => setHistoryKey((k) => k + 1)

    useEffect(() => {
        listIndicators(dashboardId)
            .then(setIndicators)
            .catch(() => setIndicators([]))
    }, [dashboardId])

    const tabClass = (active: boolean) =>
        `rounded-md px-3 py-1.5 text-sm transition-colors ${
            active
                ? 'bg-accent text-primary font-semibold'
                : 'text-foreground hover:bg-muted/60'
        }`

    return (
        <div className='grid gap-4'>
            {indicators.length === 0 ? (
                <div className='bg-warning-subtle text-warning rounded-md px-4 py-3 text-sm'>
                    이 대시보드에 연결된 평가의 체크리스트에 정량지표가 없어요.
                    대시보드를 정량지표가 있는 평가에 연결해 주세요.
                </div>
            ) : null}

            <div className='border-border bg-card inline-flex rounded-md border p-1'>
                <button
                    type='button'
                    onClick={() => setTab('upload')}
                    className={tabClass(tab === 'upload')}>
                    올리기
                </button>
                <button
                    type='button'
                    onClick={() => setTab('history')}
                    className={tabClass(tab === 'history')}>
                    적재 이력
                </button>
            </div>

            {tab === 'upload' ? (
                <>
                    <div>
                        <h2 className='text-foreground text-base font-semibold'>
                            데이터 연동
                        </h2>
                        <p className='text-muted-foreground mt-0.5 text-sm'>
                            측정값을 올리는 방법을 고르세요. 어떤 형식이든 AI가
                            체크리스트 정량지표({indicators.length}개)에 맞춰
                            읽어줍니다.
                        </p>
                    </div>

                    <CsvIngestPanel
                        dashboardId={dashboardId}
                        indicators={indicators}
                        onIngested={bumpHistory}
                    />
                    <ImageIngestPanel
                        dashboardId={dashboardId}
                        indicators={indicators}
                        onIngested={bumpHistory}
                    />
                    <ApiIngestPanel
                        dashboardId={dashboardId}
                        indicators={indicators}
                    />
                </>
            ) : (
                <IngestHistory
                    dashboardId={dashboardId}
                    refreshKey={historyKey}
                />
            )}
        </div>
    )
}
